import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import { Button, Icon, Input, Label } from '@ng-unifi/ui';
import { useAuth } from '../auth/AuthContext.js';
import { useSiteMode } from '../auth/SiteModeContext.js';
import { apiClient } from '../lib/api.js';
import { mfaSetupStart, mfaSetupVerify, type MfaSetupStartResult } from '../lib/setupStatus.js';

// GAP-AF1 (audit 2026-05-21): "Management Portal Access" reads correctly
// in commercial/single-tenant installs but wrong-foots residents in a
// multi_family install — they sign in to the same surface. The mode-aware
// subtitle lives inside SignInPage as a narrow literal (avoiding the
// Record<string, string> indexed-access string|undefined problem).

interface SignInPageProps {
  siteName: string;
}

// The page shell. Defined at module scope (NOT inside SignInPage) on
// purpose: a function declared inside the component body gets a new
// identity on every render, which makes React unmount and remount the
// entire subtree on every keystroke. That's how we ended up with an
// input that lost focus after each character typed and a "show
// password" button that appeared to do nothing — the toggle worked,
// but the input was immediately replaced with a fresh `type="password"`
// node before the change was visible.
function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <main className="bg-background min-h-screen flex flex-col items-center justify-center p-margin-mobile md:p-margin-desktop">
      <section className="relative z-10 w-full max-w-[440px] bg-surface-container-lowest p-8 md:p-10 rounded-xl shadow-sm border border-outline-variant">
        <header className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary mb-4">
            <Icon name="business" className="text-3xl" />
          </div>
          <h1 className="font-headline-md text-headline-md text-on-surface mb-2">{title}</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">{subtitle}</p>
        </header>
        {children}
      </section>
      <div className="mt-8 flex items-center space-x-2 text-on-surface-variant opacity-60">
        <Icon name="shield" fill className="text-sm" />
        <span className="font-label-md text-label-md">Secure Enterprise Access Only</span>
      </div>
    </main>
  );
}

type Stage =
  | { kind: 'credentials' }
  | {
      kind: 'mfa_setup';
      token: string;
      expiresInSec: number;
      mode: 'passkey' | 'totp';
      qr: MfaSetupStartResult | null;
    }
  | { kind: 'mfa_enrolled'; method: 'passkey' | 'totp' };

export function SignInPage({ siteName }: SignInPageProps) {
  const { signIn, verifyMfaChallenge, verifyPasskeyChallenge } = useAuth();
  const displayName = siteName?.trim() || 'Access Portal';
  const siteMode = useSiteMode();
  // Discriminated literal — avoids `Record<string, string>` indexed
  // access which TS-strict treats as `string | undefined`.
  const subtitle: string =
    siteMode === 'multi_family' ? 'Resident & Management Portal' : 'Management Portal Access';
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const [setupOtp, setSetupOtp] = useState('');
  const [challenge, setChallenge] = useState<{
    token: string;
    expiresInSec: number;
    passkeyAvailable: boolean;
    totpAvailable: boolean;
  } | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await signIn(email, password, otp || undefined);
      if (outcome.kind === 'authed') {
        navigate('/', { replace: true });
        return;
      }
      if (outcome.kind === 'mfa_challenge_required') {
        setChallenge({
          token: outcome.token,
          expiresInSec: outcome.expiresInSec,
          passkeyAvailable: outcome.passkeyAvailable,
          totpAvailable: outcome.totpAvailable,
        });
        setOtp('');
        setStage({ kind: 'credentials' });
        return;
      }
      setStage({
        kind: 'mfa_setup',
        token: outcome.token,
        expiresInSec: outcome.expiresInSec,
        mode: 'passkey',
        qr: null,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign-in failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifyChallengeOtp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyMfaChallenge(challenge.token, otp);
      navigate('/', { replace: true });
    } catch {
      setError('Invalid authenticator code');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifyPasskey() {
    if (!challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyPasskeyChallenge(challenge.token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifySetupOtp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (stage.kind !== 'mfa_setup' || stage.mode !== 'totp') return;
    setError(null);
    setSubmitting(true);
    try {
      await mfaSetupVerify({ setup_token: stage.token, otp: setupOtp });
      setStage({ kind: 'mfa_enrolled', method: 'totp' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OTP verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function startTotpFallback() {
    if (stage.kind !== 'mfa_setup') return;
    setError(null);
    setSubmitting(true);
    try {
      const qr = await mfaSetupStart(stage.token);
      setSetupOtp('');
      setStage({ ...stage, mode: 'totp', qr });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start authenticator setup');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSetupPasskey(attachment?: 'platform' | 'cross-platform') {
    if (stage.kind !== 'mfa_setup') return;
    setError(null);
    setSubmitting(true);
    try {
      const start = await apiClient.post<{
        data: { options: Parameters<typeof startRegistration>[0]['optionsJSON'] };
      }>(
        '/api/v1/auth/passkeys/setup/start',
        attachment ? { attachment } : {},
        { headers: { 'x-mfa-setup-token': stage.token } },
      );
      const credential = await startRegistration({ optionsJSON: start.data.options });
      await apiClient.post(
        '/api/v1/auth/passkeys/setup/complete',
        { credential, nickname: 'Primary device' },
        { headers: { 'x-mfa-setup-token': stage.token } },
      );
      setStage({ kind: 'mfa_enrolled', method: 'passkey' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey setup failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (stage.kind === 'mfa_setup' && stage.mode === 'passkey') {
    return (
      <Shell
        title="Set up passkey"
        subtitle="Use Face ID, fingerprint, device PIN, or a security key as your second factor."
      >
        <div className="space-y-6">
          <Button
            type="button"
            className="w-full"
            onClick={() => void onSetupPasskey('platform')}
            loading={submitting}
          >
            <Icon name="fingerprint" />
            Set up passkey
          </Button>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void onSetupPasskey('cross-platform')}
              disabled={submitting}
            >
              <Icon name="phone_iphone" />
              Phone or security key
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void startTotpFallback()}
              disabled={submitting}
            >
              Use authenticator app
            </Button>
          </div>
          {error ? (
            <p role="alert" className="font-body-md text-body-md text-error">
              {error}
            </p>
          ) : null}
        </div>
      </Shell>
    );
  }

  if (stage.kind === 'mfa_setup' && stage.mode === 'totp') {
    return (
      <Shell
        title="Set up authenticator app"
        subtitle="Scan the QR with an authenticator app, then enter the 6-digit code."
      >
        <form className="space-y-6" onSubmit={onVerifySetupOtp} noValidate>
          {stage.qr ? (
            <div className="flex flex-col items-center gap-3">
              <img
                src={stage.qr.qr_png_data_url}
                alt="TOTP QR code"
                className="h-48 w-48 rounded-lg border border-outline-variant"
              />
              <p className="break-all text-xs text-on-surface-variant">
                or paste secret: <code className="font-code text-code">{stage.qr.secret}</code>
              </p>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label
              htmlFor="setup-otp"
              className="font-label-md text-label-md text-on-surface block px-1"
            >
              Authenticator code
            </Label>
            <Input
              id="setup-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              required
              value={setupOtp}
              onChange={(e) => setSetupOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={submitting}
            />
          </div>
          {error ? (
            <p role="alert" className="font-body-md text-body-md text-error">
              {error}
            </p>
          ) : null}
          <Button type="submit" className="w-full" loading={submitting}>
            Verify and enroll
          </Button>
        </form>
      </Shell>
    );
  }

  if (stage.kind === 'mfa_enrolled') {
    const isPasskey = stage.method === 'passkey';
    return (
      <Shell
        title={isPasskey ? 'Passkey enrolled' : 'Authenticator enrolled'}
        subtitle={
          isPasskey
            ? 'Passkey set up. Sign in again with your password and passkey.'
            : 'Authenticator set up. Sign in again with your password and the 6-digit code.'
        }
      >
        <Button
          className="w-full"
          onClick={() => {
            setStage({ kind: 'credentials' });
            setPassword('');
            setOtp('');
            setChallenge(null);
            setError(null);
          }}
        >
          Sign in
        </Button>
      </Shell>
    );
  }

  if (challenge) {
    return (
      <Shell
        title={challenge.passkeyAvailable ? 'Confirm sign-in' : 'Enter authenticator code'}
        subtitle={
          challenge.passkeyAvailable && challenge.totpAvailable
            ? 'Use your passkey, or enter the 6-digit code from your authenticator app.'
            : challenge.passkeyAvailable
              ? 'Use your passkey to finish signing in.'
            : 'Use the 6-digit code from your authenticator app.'
        }
      >
        <form className="space-y-6" onSubmit={onVerifyChallengeOtp} noValidate>
          {challenge.passkeyAvailable ? (
            <>
              <Button
                type="button"
                className="w-full"
                onClick={() => void onVerifyPasskey()}
                loading={submitting}
              >
                <Icon name="fingerprint" />
                Use passkey
              </Button>
              {challenge.totpAvailable ? (
                <div className="flex items-center gap-3 text-on-surface-variant">
                  <div className="h-px flex-1 bg-outline-variant" />
                  <span className="font-label-md text-label-md">or use authenticator app</span>
                  <div className="h-px flex-1 bg-outline-variant" />
                </div>
              ) : null}
            </>
          ) : null}
          {challenge.totpAvailable ? (
            <div className="space-y-2">
              <Label
                htmlFor="otp"
                className="font-label-md text-label-md text-on-surface block px-1"
              >
                Authenticator code
              </Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={submitting}
                autoFocus={!challenge.passkeyAvailable}
              />
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="font-body-md text-body-md text-error">
              {error}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setChallenge(null);
                setOtp('');
                setError(null);
              }}
              disabled={submitting}
            >
              Back
            </Button>
            {challenge.totpAvailable ? (
              <Button type="submit" className="flex-1" loading={submitting}>
                Verify
              </Button>
            ) : null}
          </div>
        </form>
      </Shell>
    );
  }

  return (
    <Shell title={displayName} subtitle={subtitle}>
      <form className="space-y-6" onSubmit={onSubmit} noValidate>
        <div className="space-y-2">
          <Label htmlFor="email" className="font-label-md text-label-md text-on-surface block px-1">
            Email Address
          </Label>
          <div className="relative group">
            <Icon
              name="mail"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary transition-colors"
            />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              placeholder="name@example.com"
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <Label htmlFor="password" className="font-label-md text-label-md text-on-surface block">
              Password
            </Label>
            <Link
              to="/forgot-password"
              className="font-label-md text-label-md text-primary hover:underline transition-all"
            >
              Forgot Password?
            </Link>
          </div>
          <div className="relative group">
            <Icon
              name="lock"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary transition-colors"
            />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              placeholder="••••••••"
              className="pl-10 pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-outline-variant hover:text-outline"
            >
              <Icon name={showPassword ? 'visibility_off' : 'visibility'} />
            </button>
          </div>
        </div>

        {error ? (
          <p role="alert" className="font-body-md text-body-md text-error">
            {error}
          </p>
        ) : null}

        <Button type="submit" className="w-full" loading={submitting}>
          <span className="font-label-md text-label-md">Sign In</span>
          <Icon name="login" className="ml-2" />
        </Button>
      </form>
    </Shell>
  );
}
